import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';

const JobFeed = () => {
  const { toast } = useToast();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showBidForm, setShowBidForm] = useState(false);
  const [bidData, setBidData] = useState({
    bidAmount: '',
    estimatedDuration: '',
    description: '',
    availableDate: ''
  });
  const [submittingBid, setSubmittingBid] = useState(false);

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('dcTech_token') || localStorage.getItem('token');
      
      const response = await fetch(`${backendUrl}/api/jobs?status=open`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatBudget = (budget, budgetType) => {
    if (!budget) return 'Budget: Negotiable';
    
    const formattedAmount = `$${budget.toLocaleString()}`;
    switch (budgetType) {
      case 'fixed':
        return `Budget: ${formattedAmount} (Fixed)`;
      case 'hourly':
        return `Budget: ${formattedAmount}/hour`;
      default:
        return `Budget: ~${formattedAmount} (Negotiable)`;
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'urgent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'normal':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'flexible':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const posted = new Date(date);
    const diffInHours = Math.floor((now - posted) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just posted';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const handleBidSubmit = async (e) => {
    e.preventDefault();
    setSubmittingBid(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('dcTech_token') || localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/jobs/${selectedJob.id}/bids`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jobId: selectedJob.id,
          bidAmount: parseFloat(bidData.bidAmount),
          estimatedDuration: bidData.estimatedDuration,
          description: bidData.description,
          availableDate: bidData.availableDate
        })
      });

      if (response.ok) {
        toast({
          title: "Bid Submitted Successfully! 🎯",
          description: "Your bid has been sent to the customer.",
          className: "bg-green-50 border-green-200 text-green-800"
        });

        // Reset form and close modal
        setBidData({
          bidAmount: '',
          estimatedDuration: '',
          description: '',
          availableDate: ''
        });
        setShowBidForm(false);
        setSelectedJob(null);
        
        // Reload jobs to update bid status
        loadJobs();
      } else {
        const errorData = await response.json();
        toast({
          title: "Error",
          description: errorData.detail || "Failed to submit bid",
          className: "bg-red-50 border-red-200 text-red-800"
        });
      }
    } catch (error) {
      console.error('Error submitting bid:', error);
      toast({
        title: "Error",
        description: "Failed to submit bid. Please try again.",
        className: "bg-red-50 border-red-200 text-red-800"
      });
    } finally {
      setSubmittingBid(false);
    }
  };

  const handleBidInputChange = (e) => {
    const { name, value } = e.target;
    setBidData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mr-4"></div>
        <span className="text-white">Loading jobs...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Available Jobs</h2>
        <Button
          onClick={loadJobs}
          className="bg-white/20 bg-white/15 text-white border-white/30"
        >
          🔄 Refresh
        </Button>
      </div>

      {jobs.length === 0 ? (
        <Card className="bg-white/10 backdrop-blur-lg border-white/20">
          <CardContent className="text-center py-8">
            <p className="text-white/70 text-lg">No jobs available at the moment.</p>
            <p className="text-white/50 text-sm mt-2">Check back later for new opportunities!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {jobs.map((job) => (
            <Card key={job.id} className="bg-white/10 backdrop-blur-lg border-white/20 bg-white/10 ">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-grow">
                    <CardTitle className="text-white text-xl mb-2">{job.title}</CardTitle>
                    <div className="flex items-center space-x-4 text-sm text-white/70">
                      <span>📍 {job.location}</span>
                      <span>👤 {job.customerName}</span>
                      <span>⏰ {getTimeAgo(job.createdAt)}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    <Badge className={getUrgencyColor(job.urgency)}>
                      {job.urgency.toUpperCase()}
                    </Badge>
                    <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                      {job.requiredTrade}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-white/80 mb-4">{job.description}</p>
                
                <div className="flex items-center justify-between mb-4">
                  <span className="text-white font-semibold">
                    {formatBudget(job.budget, job.budgetType)}
                  </span>
                  <span className="text-white/70 text-sm">
                    💼 {job.bidsCount} bid{job.bidsCount !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Job Images */}
                {job.images && job.images.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {job.images.slice(0, 3).map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`Job image ${index + 1}`}
                        className="w-full h-20 object-cover rounded"
                      />
                    ))}
                    {job.images.length > 3 && (
                      <div className="w-full h-20 bg-white/20 rounded flex items-center justify-center text-white/70 text-sm">
                        +{job.images.length - 3} more
                      </div>
                    )}
                  </div>
                )}

                <div className="flex space-x-3">
                  {job.hasUserBid ? (
                    <Button disabled className="bg-gray-500 text-gray-300 cursor-not-allowed">
                      ✓ Already Bid
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        setSelectedJob(job);
                        setShowBidForm(true);
                      }}
                      className="bg-gradient-to-r from-green-500 to-blue-500   text-white"
                    >
                      📝 Submit Bid
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="border-white/50 text-white bg-white/10"
                  >
                    👁️ View Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Bid Form Modal */}
      {showBidForm && selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-white max-w-md w-full">
            <CardHeader>
              <CardTitle className="text-gray-800">Submit Bid for: {selectedJob.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBidSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="bidAmount">Your Bid Amount ($) *</Label>
                  <Input
                    id="bidAmount"
                    name="bidAmount"
                    type="number"
                    step="0.01"
                    value={bidData.bidAmount}
                    onChange={handleBidInputChange}
                    placeholder="e.g., 500.00"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="estimatedDuration">Estimated Duration *</Label>
                  <Input
                    id="estimatedDuration"
                    name="estimatedDuration"
                    value={bidData.estimatedDuration}
                    onChange={handleBidInputChange}
                    placeholder="e.g., 2-3 days, 1 week"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="availableDate">Available Start Date *</Label>
                  <Input
                    id="availableDate"
                    name="availableDate"
                    type="date"
                    value={bidData.availableDate}
                    onChange={handleBidInputChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Bid Description *</Label>
                  <textarea
                    id="description"
                    name="description"
                    value={bidData.description}
                    onChange={handleBidInputChange}
                    placeholder="Describe your approach, materials you'll use, why you're the best choice..."
                    required
                    rows={3}
                    className="w-full border rounded-md px-3 py-2 resize-none"
                  />
                </div>

                <div className="flex space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowBidForm(false);
                      setSelectedJob(null);
                      setBidData({
                        bidAmount: '',
                        estimatedDuration: '',
                        description: '',
                        availableDate: ''
                      });
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submittingBid}
                    className="flex-1 bg-gradient-to-r from-green-500 to-blue-500   text-white"
                  >
                    {submittingBid ? 'Submitting...' : 'Submit Bid'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default JobFeed;